import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { getImageUrl, getOrderAreaImageUrl, getReferralBrandLogoUrl } from 'helpers';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { UserService } from 'shared/services';

@Injectable({ providedIn: 'root' })
export class ImageModel {
    constructor(
        private http: HttpClient,
        private userService: UserService
    ) {}

    private static secureUrlCache: Record<string, string> = {};

    public buildSecureImageUrl(imageId: string, size: string, extension?: string): Observable<string> {
        if (!imageId) {
            return of(null);
        }

        return this.getSafeImageUrl(getImageUrl(imageId, size, extension));
    }

    public buildImageUrl(imageId: string, size: string, extension?: string) {
        return getImageUrl(imageId, size, extension);
    }

    public buildSecureOrderAreaImageUrl(imageId: string, size: string): Observable<string> {
        if (!imageId) {
            return of(null);
        }

        return this.getSafeImageUrl(getOrderAreaImageUrl(imageId, size));
    }

    public buildOrderAreaImageUrl(imageId: string, size: string): string {
        return getOrderAreaImageUrl(imageId, size);
    }

    public buildReferralBrandLogoUrl(referralId: string): string {
        return getReferralBrandLogoUrl(referralId);
    }

    public getSafeImageUrl(link: string): Observable<string> {
        if (!link) return of(null);

        try {
            const url = new URL(link);
            const requestUrl = url.toString();

            if (!url.origin || !url.pathname) {
                return of(link);
            }

            if (ImageModel.secureUrlCache[requestUrl]) {
                return of(ImageModel.secureUrlCache[requestUrl]);
            }

            this.shrinkCacheIfOverflown();

            return this.http
                .get(requestUrl, {
                    headers: {
                        Authorization: `Bearer ${this.userService.getAuthToken()}`
                    },
                    responseType: 'blob'
                })
                .pipe(
                    map((response) => URL.createObjectURL(response)),
                    tap((objectUrl) => (ImageModel.secureUrlCache[requestUrl] = objectUrl)),
                    catchError(() => of(null))
                );
        } catch (e) {
            return of(link);
        }
    }

    private shrinkCacheIfOverflown() {
        const cacheKeys = Object.keys(ImageModel.secureUrlCache);

        if (cacheKeys.length >= 300) {
            cacheKeys.slice(0, 50).forEach((key) => {
                URL.revokeObjectURL(ImageModel.secureUrlCache[key]);
                delete ImageModel.secureUrlCache[key];
            });
        }
    }
}
